import { FunctionComponent } from "react";

import { classNames } from "@/utils/classNames";

import { INVITE_NOTE_LIMIT, INVITE_NOTE_PREMIUM_LIMIT } from "../utils/inviteNote.constants";
import { getInviteNoteSeverity } from "../utils/inviteNoteSeverity.util";

import type { LinkedinTier } from "@/utils/getLinkedinTier";

interface InviteNoteCharCounterProps {
	charCount: number;
	className?: string;
	/**
	 * The sender's LinkedIn tier. Free senders warn on any non-empty note and error
	 * past 200 characters; Premium / Sales Navigator senders warn past 200 and error
	 * past 300. Defaults to the lenient (premium) thresholds when omitted.
	 */
	tier?: LinkedinTier;
}

export const InviteNoteCharCounter: FunctionComponent<InviteNoteCharCounterProps> = ({
	charCount,
	className,
	tier,
}) => {
	const severity = getInviteNoteSeverity(charCount, tier);
	const isError = severity === "error";
	const isWarning = severity === "warning";
	const isFree = tier === "free";
	const displayLimit = isFree
		? INVITE_NOTE_LIMIT
		: isError || isWarning
			? INVITE_NOTE_PREMIUM_LIMIT
			: INVITE_NOTE_LIMIT;

	// Inline message shown before the length indicator for warning / error states.
	const message = isFree
		? isError
			? `LinkedIn rejects invite notes longer than ${INVITE_NOTE_LIMIT} characters. Shorten the message before sending.`
			: isWarning
				? `LinkedIn Free accounts can only send about 5 connection requests with a note per month — consider upgrading to Premium.`
				: null
		: isError
			? `LinkedIn rejects invite notes longer than ${INVITE_NOTE_PREMIUM_LIMIT} characters. Shorten the message before sending.`
			: isWarning
				? `Standard LinkedIn invite notes are limited to ${INVITE_NOTE_LIMIT} characters. Up to ${INVITE_NOTE_PREMIUM_LIMIT} is only allowed for users with LinkedIn Premium.`
				: null;

	return (
		<div className="flex items-center gap-1.5">
			{message && (
				<span
					className={classNames(
						"text-[10px] font-medium",
						isError ? "text-status-error bg-status-error-light" : "text-status-warning"
					)}
				>
					{message}
				</span>
			)}
			<span
				className={classNames(
					"text-[10px] whitespace-nowrap",
					isError
						? "text-status-error font-semibold"
						: isWarning
							? "text-status-warning font-semibold"
							: "text-text-meta",
					className
				)}
			>
				{charCount}/{displayLimit} characters
			</span>
		</div>
	);
};
